/* eslint-disable react/prop-types */
/* eslint-disable react/no-danger */
import { useState, FC, useEffect } from 'react'
import styled from 'styled-components'
import '../GPConnect.css'
import { CrashIcon, EmptyState, PermissionIcon, RangePicker } from '@lmc/ui-kit'
import { Skeleton } from 'antd'
import dayjs from 'dayjs'
import { useGPdata } from '../hooks/useGPdata'
import { GPBanner } from './GPBanner'
import { NAMESPACE } from 'locale'
import { useTranslation } from 'react-i18next'
import { gpConnectConstants } from 'locale/gpConnect'
import {
  CommonErrors,
  SupersededData,
  GPConnectBanner,
  GP_TYPE,
  GP_TYPE_WITH_DATE_FILTER,
} from '@lmc/ts-common'
import {
  EmptyStateDescription,
  HereDescription,
} from '../components/commonStyledComponents'
import { Link } from 'react-router-dom'
import { useRouter } from 'hooks/useRouter'

interface GPConnectRecordProps {
  type: GP_TYPE
}

export const GP_NOT_FOUND_ERROR_MESSAGE = 'Patient Record Not Found'

export const GP_NHS_INVALID_ERROR_MESSAGE = 'NHS number invalid'

const dateFormat = 'YYYY-MM-DD'

export const GPConnectRecord: FC<GPConnectRecordProps> = ({
  type,
}: GPConnectRecordProps) => {
  const gpTypesWithDate = GP_TYPE_WITH_DATE_FILTER || [
    GP_TYPE.ENC,
    GP_TYPE.CLI,
    GP_TYPE.PRB,
    GP_TYPE.MED,
    GP_TYPE.REF,
    GP_TYPE.OBS,
    GP_TYPE.ADM,
  ]
  const hasDateFilter = gpTypesWithDate.includes(type)

  const { t } = useTranslation(NAMESPACE.GP_CONNECT)

  const {
    params: { id },
  } = useRouter()

  const nowDate = dayjs()

  const before2YearsDate = dayjs().subtract(2, 'year')

  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(nowDate)

  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(
    before2YearsDate
  )

  useEffect(() => {
    if (hasDateFilter) {
      setEndDate(nowDate)
      setStartDate(before2YearsDate)
    }
  }, [type])

  const {
    data: gpData,
    isLoading,
    error,
  } = useGPdata(
    type,
    hasDateFilter
      ? {
          startDate: startDate?.toISOString(),
          endDate: endDate?.toISOString(),
        }
      : {}
  )

  const bannerData = gpData?.banner
  const supersededData = gpData?.supersededData

  if ((error || !bannerData) && !isLoading) {
    if (error?.message === GP_NHS_INVALID_ERROR_MESSAGE) {
      return (
        <div>
          <EmptyState
            title={t(gpConnectConstants.THIS_PATIENT_1)}
            icon={<PermissionIcon />}
            description={
              <EmptyStateDescription>
                {t(gpConnectConstants.THIS_PATIENT_2)}
                <Link to={`/residents/${id}/profile`}>
                  <HereDescription>
                    {' '}
                    {t(gpConnectConstants.THIS_PATIENT_3)}
                  </HereDescription>
                </Link>
                {t(gpConnectConstants.THIS_PATIENT_4)}
              </EmptyStateDescription>
            }
          />
        </div>
      )
    }
    return (
      <div>
        <EmptyState
          title={(error?.message as string) || t(gpConnectConstants.NOT_FOUND)}
          icon={<CrashIcon />}
          description={getErrorDescription(error?.code, t)}
        />
      </div>
    )
  }

  return (
    <div>
      <GPBanner
        bannerData={bannerData as GPConnectBanner}
        supersededData={supersededData as SupersededData}
        residentId={id}
      />
      {hasDateFilter && (
        <RangePicker
          format={dateFormat}
          defaultValue={[startDate, endDate]}
          disabledDate={(date) => date.isAfter(dayjs())}
          value={[startDate, endDate]}
          onChange={(values) => {
            setStartDate(values?.length ? values[0] : null)
            setEndDate(values?.length ? values[1] : null)
          }}
        />
      )}
      {isLoading ? (
        <Spinner />
      ) : (
        <div
          className="gp-connect-wrapper"
          dangerouslySetInnerHTML={{ __html: gpData?.htmlString || '' }}
        />
      )}
    </div>
  )
}

const Spinner = styled(Skeleton)`
  padding-top: 24px;
`

const getErrorDescription = (
  errorCode: string | CommonErrors.Codes | undefined,
  t: (value: string) => string
) => {
  switch (errorCode) {
    case CommonErrors.Codes.deceasedRecord || 1048:
      return t(gpConnectConstants.ERROR_DECEASED_PATIENT)
    case CommonErrors.Codes.sensitiveRecord || 1049:
      return t(gpConnectConstants.ERROR_SENSITIVE_PATIENT)
    default:
      return t(gpConnectConstants.OPS)
  }
}
